// ── KYC proposed config (v2), sectionized by config file ────────────────────
//
// Mockup only (local state, never written to the workflow JSON). In v2 the
// workflow engine handles orchestration, so per-customer you configure the data
// source CONNECTOR for this KYC node's provider, plus risk scoring:
//   con_dvs / con_equifax / con_experian / con_superchoice — datasource creds & settings
//   risks — risk scoring weights & thresholds
// entity_profiles / routes / custom_kyc_check_types are NOT configured per
// customer in v2 and are intentionally omitted.
import { useState } from 'react'
import { Checkbox, Field, SectionLabel, TextInput, Toggle } from './fields'
import { ConfigSection } from './ConfigSection'

export const KYC_PROVIDER_LABELS = ['DVS', 'Equifax', 'Experian', 'SuperChoice', 'Manual'] as const
export type KycProviderLabel = typeof KYC_PROVIDER_LABELS[number]

// Map a KYC node providerName to its connector config file.
export function connectorFor(provider: string): 'con_dvs' | 'con_equifax' | 'con_experian' | 'con_superchoice' | 'manual' | 'unknown' {
  const p = provider.toLowerCase()
  if (p === 'dvs') return 'con_dvs'
  if (p.startsWith('equifax')) return 'con_equifax'
  if (p.startsWith('experian')) return 'con_experian'
  if (p === 'superchoice') return 'con_superchoice'
  if (p === 'manual') return 'manual'
  return 'unknown'
}

const DVS_SERVICES = ['full_entity', 'entity_doc_only', 'entity_only', 'document']

function DvsSection() {
  const [services, setServices] = useState<string[]>(['full_entity', 'entity_doc_only', 'document'])
  const [electoralRoll, setElectoralRoll] = useState(true)
  const [allowDocs, setAllowDocs] = useState(true)
  const [onlyLatest, setOnlyLatest] = useState(true)
  const [endpoint, setEndpoint] = useState('https://dvs.dvshub.com.au:19443/Bus/VerificationServiceBus.svc/https')
  const [agency, setAgency] = useState('ONE001')
  const [version, setVersion] = useState('2')
  const [cert, setCert] = useState('SECRET:.../dvs/cert')
  const toggle = (s: string) => setServices((c) => (c.includes(s) ? c.filter((x) => x !== s) : [...c, s]))
  return (
    <ConfigSection title="DVS connector" file="con_dvs" defaultOpen>
      <Field label="Enabled services">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {DVS_SERVICES.map((s) => (
            <Checkbox key={s} label={s} checked={services.includes(s)} onChange={() => toggle(s)} />
          ))}
        </div>
      </Field>
      <SectionLabel>Dataset</SectionLabel>
      <Toggle label="Allow electoral roll" checked={electoralRoll} onChange={setElectoralRoll} />
      <Toggle label="Allow documents" checked={allowDocs} onChange={setAllowDocs} />
      <Toggle label="Only latest docs" checked={onlyLatest} onChange={setOnlyLatest} />
      <Field label="Endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="DVS agency code"><TextInput value={agency} onChange={(e) => setAgency(e.target.value)} placeholder="ONE001" /></Field>
      <Field label="Version number"><TextInput value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2" /></Field>
      <Field label="TLS cert (secret path)"><TextInput value={cert} onChange={(e) => setCert(e.target.value)} placeholder="SECRET:…" /></Field>
    </ConfigSection>
  )
}

function EquifaxSection() {
  const [endpoint, setEndpoint] = useState('https://apiconnect.equifax.com.au/sys2/idmatrix-v4')
  const [username, setUsername] = useState('SECRET:.../equifax/username')
  const [password, setPassword] = useState('SECRET:.../equifax/password')
  const [configName, setConfigName] = useState('Frankie_GovernmentSources')
  const [reuseHours, setReuseHours] = useState('167')
  const [allowDvs, setAllowDvs] = useState(true)
  const [allowMobile, setAllowMobile] = useState(false)
  const [allowEmail, setAllowEmail] = useState(false)
  return (
    <ConfigSection title="Equifax connector (IDMatrix)" file="con_equifax" defaultOpen>
      <Field label="Endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="Username (secret path)"><TextInput value={username} onChange={(e) => setUsername(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Password (secret path)"><TextInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Configuration name"><TextInput value={configName} onChange={(e) => setConfigName(e.target.value)} placeholder="Frankie_GovernmentSources" /></Field>
      <Field label="Enquiry ID reuse (hours)"><TextInput type="number" value={reuseHours} onChange={(e) => setReuseHours(e.target.value)} placeholder="167" /></Field>
      <SectionLabel>Dataset</SectionLabel>
      <Toggle label="Allow DVS" checked={allowDvs} onChange={setAllowDvs} />
      <Toggle label="Allow mobile" checked={allowMobile} onChange={setAllowMobile} />
      <Toggle label="Allow email" checked={allowEmail} onChange={setAllowEmail} />
      <p className="text-[10px] text-[#5b6b85]">Service variants: default · under18 · with_credit_header (separate creds).</p>
    </ConfigSection>
  )
}

function ExperianSection() {
  const [endpoint, setEndpoint] = useState('https://creditservices.experian.com.au/nextgen-aus-pds/cxf/reporting-WS')
  const [entityCode, setEntityCode] = useState('20')
  const [productCode, setProductCode] = useState('ANIDV0001')
  const [bureauId, setBureauId] = useState('SECRET:.../experian/bureau_id')
  const [accountId, setAccountId] = useState('SECRET:.../experian/account_id')
  const [password, setPassword] = useState('SECRET:.../experian/password')
  return (
    <ConfigSection title="Experian connector" file="con_experian" defaultOpen>
      <Field label="URI endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="Entity verification code"><TextInput value={entityCode} onChange={(e) => setEntityCode(e.target.value)} placeholder="20" /></Field>
      <Field label="Product ID verification code"><TextInput value={productCode} onChange={(e) => setProductCode(e.target.value)} placeholder="ANIDV0001" /></Field>
      <Field label="Bureau ID (secret path)"><TextInput value={bureauId} onChange={(e) => setBureauId(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Account ID (secret path)"><TextInput value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Password (secret path)"><TextInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="SECRET:…" /></Field>
    </ConfigSection>
  )
}

function SuperchoiceSection() {
  const [endpoint, setEndpoint] = useState('https://dataservices-idv.superchoice.com.au/v1/identityVerification/person')
  const [username, setUsername] = useState('SECRET:.../superchoice/username')
  const [password, setPassword] = useState('SECRET:.../superchoice/password')
  const [segment, setSegment] = useState('3')
  const [pattern, setPattern] = useState('A')
  const [useMiddleName, setUseMiddleName] = useState(false)
  return (
    <ConfigSection title="SuperChoice connector" file="con_superchoice" defaultOpen>
      <Field label="Endpoint"><TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} /></Field>
      <Field label="Username (secret path)"><TextInput value={username} onChange={(e) => setUsername(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Password (secret path)"><TextInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="SECRET:…" /></Field>
      <Field label="Industry segment code"><TextInput value={segment} onChange={(e) => setSegment(e.target.value)} placeholder="3" /></Field>
      <Field label="Matching pattern"><TextInput value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="A" /></Field>
      <Toggle label="Use middle name" checked={useMiddleName} onChange={setUseMiddleName} />
    </ConfigSection>
  )
}

function RisksSection() {
  const [pepWeight, setPepWeight] = useState('40')
  const [docFailWeight, setDocFailWeight] = useState('25')
  const [mediumAt, setMediumAt] = useState('30')
  const [highAt, setHighAt] = useState('60')
  return (
    <ConfigSection title="Risk scoring" file="risks">
      <Field label="PEP match weight"><TextInput type="number" value={pepWeight} onChange={(e) => setPepWeight(e.target.value)} /></Field>
      <Field label="Document failure weight"><TextInput type="number" value={docFailWeight} onChange={(e) => setDocFailWeight(e.target.value)} /></Field>
      <Field label="MEDIUM risk threshold"><TextInput type="number" value={mediumAt} onChange={(e) => setMediumAt(e.target.value)} /></Field>
      <Field label="HIGH risk threshold"><TextInput type="number" value={highAt} onChange={(e) => setHighAt(e.target.value)} /></Field>
    </ConfigSection>
  )
}

// `providers` are the rep-picked KYC_PROVIDER_LABELS for this node — plural
// because a real KYC + Cascade node can route through more than one bureau,
// not just whichever single one (if any) the imported workflow JSON names.
export function KycProposedConfig({ providers }: { providers: readonly string[] }) {
  const connectors = new Set(providers.map((p) => connectorFor(p)))
  return (
    <>
      {connectors.size === 0 && (
        <p className="text-[11px] text-[#8fa0bd]">Pick at least one provider above to see its connector config.</p>
      )}
      {connectors.has('con_dvs') && <DvsSection />}
      {connectors.has('con_equifax') && <EquifaxSection />}
      {connectors.has('con_experian') && <ExperianSection />}
      {connectors.has('con_superchoice') && <SuperchoiceSection />}
      {connectors.has('manual') && (
        <ConfigSection title="Manual KYC" file="—" defaultOpen>
          <p className="text-[11px] text-[#8fa0bd]">Manual KYC has no external data-source connector.</p>
        </ConfigSection>
      )}
      <RisksSection />
    </>
  )
}
